import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { updateConversation } from '../store/conversationSlice';

const RenameConversationModal = ({ 
  open, 
  onClose, 
  conversation, 
  onSuccess 
}) => {
  const dispatch = useDispatch();
  const [newTitle, setNewTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update title when conversation changes or modal opens
  useEffect(() => {
    if (open && conversation) {
      setNewTitle(conversation.title || 'Untitled Chat');
    }
  }, [open, conversation]);

  const handleClose = () => {
    setNewTitle('');
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (conversation && newTitle.trim() && !isSubmitting) {
      setIsSubmitting(true);
      try {
        await dispatch(updateConversation({
          conversation_id: conversation.conversation_id,
          title: newTitle.trim(),
        })).unwrap();
        
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error('Error renaming conversation:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      aria-labelledby="rename-dialog-title"
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.1)', // lighter dark
        }
      }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '20px',
          background: 'rgb(255, 255, 255, 0.9)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          border: '2px solid rgba(200, 200, 200, 0.3)',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      <DialogTitle id="rename-dialog-title" sx={{ marginTop: '10px', marginLeft: '3px', padding: '10px 24px' }}>Rename Chat</DialogTitle>
      <DialogContent>
      <TextField
        autoFocus
        margin="dense"
        fullWidth
        value={newTitle}
        onChange={(e) => setNewTitle(e.target.value)}
        variant="outlined"
        aria-label="Chat name"
        onKeyPress={handleKeyPress}
        sx={{
            '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
            '& fieldset': {
                borderColor: 'rgba(180, 180, 180, 0.4)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(180, 180, 180, 0.4)',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'rgba(180, 180, 180, 0.4)',
            },
            '&.Mui-focused': {
                boxShadow: 'none', // remove any default glow
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
            },
            },
            '& input': {
            color: '#333',
            },
        }}
        />
      </DialogContent>
      <DialogActions sx={{ marginBottom: '10px', marginRight: '17px' }}>
        <Button 
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.4)',
            border: '1px solid rgba(150, 150, 150, 0.3)',
            color: '#71717A',
            cursor: 'pointer',
            transition: '0.3s ease',
            backdropFilter: 'blur(6px)',
            textTransform: 'none',
            width: '100px',
            '&:hover': {
                background: 'rgba(255, 255, 255, 0.6)',
                borderColor: 'rgba(150, 150, 150, 0.4)',
                boxShadow: '0 0 10px rgba(18, 157, 232, 0.15)',
            },
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={isSubmitting}
          sx={{
            borderRadius: '12px',
            background: 'rgba(18, 157, 232, 0.1)',
            boxShadow: 'none',
            border: '1px solid rgba(18, 157, 232, 0.2)',
            color: '#71717A',
            cursor: 'pointer',
            transition: '0.3s ease',
            backdropFilter: 'blur(6px)',
            textTransform: 'none',
            width: '100px',
            '&:hover': {
                background: 'rgba(18, 157, 232, 0.2)',
                borderColor: 'rgba(18, 157, 232, 0.5)',
                boxShadow: '0 0 10px rgba(18, 157, 232, 0.15)',
            },
          }}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameConversationModal; 